import { useSelector } from "@app/react";
import { homeUrl } from "@app/routes";
import {
  STATUS_WARNING_ICON_HEX,
  type StatusVariant,
  variantToClassName,
} from "@app/status-variant";
import { selectIsUserAuthenticated } from "@app/token";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { IconAlertTriangle, IconCheck, IconInfo } from "./icons";
import { LoadingSpinner } from "./loading";

const VariantIcon = ({ variant = "info" }: { variant: StatusVariant }) => {
  if (variant === "error") {
    return <IconAlertTriangle color="#AD1A1A" variant="base" />;
  }

  if (variant === "warning") {
    return <IconAlertTriangle color={STATUS_WARNING_ICON_HEX} variant="base" />;
  }

  if (variant === "success") {
    return (
      <IconCheck
        color="#00633F"
        variant="base"
        strokeWidth={1.5}
        style={{ transform: "scale(0.9)" }}
      />
    );
  }

  if (variant === "info") {
    return <IconInfo variant="base" />;
  }

  if (variant === "progress") {
    return <LoadingSpinner color="#364ECC" variant="base" />;
  }

  return <IconInfo variant="base" />;
};

export const Banner = ({
  children,
  className = "",
  variant = "info",
  showIcon = true,
  iconAlign = "start",
}: {
  children: React.ReactNode;
  className?: string;
  variant?: StatusVariant;
  showIcon?: boolean;
  iconAlign?: "start" | "center";
}) => {
  const classes = variantToClassName(variant);

  return (
    <div
      role="status"
      className={classNames(
        classes,
        "rounded-md px-3 py-3 shadow flex",
        iconAlign === "center" ? "items-center" : "items-start",
        className,
      )}
    >
      {showIcon ? (
        <div className="mr-3">
          <VariantIcon variant={variant} />
        </div>
      ) : null}
      <div className={showIcon ? undefined : "min-w-0 flex-1"}>{children}</div>
    </div>
  );
};

export const BannerMessagesBasic = ({
  isSuccess,
  isError,
  isLoading,
  message,
  className,
}: {
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
  message: string;
  className?: string;
}) => {
  if (!message) return null;

  if (isLoading) {
    return (
      <div className={`flex items-center text-indigo ${className}`}>
        {message}
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className={`flex items-center text-green-300 ${className}`}>
        <IconCheck color="#4D9279" />
        {message}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`flex items-center text-red-300 ${className}`}>
        <IconAlertTriangle color="#C76464" />
        {message}
      </div>
    );
  }

  return null;
};

export const BannerMessages = ({
  isSuccess,
  isError,
  isWarning,
  message,
  className,
}: {
  isSuccess: boolean;
  isError: boolean;
  isWarning?: boolean;
  message: React.ReactNode;
  className?: string;
}) => {
  if (!message) return null;

  if (isSuccess) {
    return (
      <Banner className={className} variant="success">
        {message}
      </Banner>
    );
  }

  if (isWarning) {
    return (
      <Banner className={className} variant="warning">
        {message}
      </Banner>
    );
  }

  if (isError) {
    return (
      <Banner className={className} variant="error">
        {message}
      </Banner>
    );
  }

  return null;
};

export const AlreadyAuthenticatedBanner = () => {
  const isAuthenticated = useSelector(selectIsUserAuthenticated);
  return isAuthenticated ? (
    <Banner variant="success">
      You are already logged in!{" "}
      <Link to={homeUrl()} className="underline">
        Continue to dashboard
      </Link>
    </Banner>
  ) : null;
};
